/**
 * The bridge between "the server gave me a URL" and "an `<img>` can show it".
 *
 * Every place that renders a photograph goes through this. The work is async
 * and rendering is not, so the hook holds the result in state, and it retains
 * the decrypted copy for exactly as long as the component is showing it.
 *
 * Laziness comes from the caller: the grid is virtualised, so only tiles on
 * screen ever mount, and only those ever fetch. That replaces the `loading`
 * attribute, which stops helping once the browser is not the one fetching.
 */

import { useEffect, useState } from "react";
import {
  LockedError,
  StorageUnreachableError,
  acquireDecryptedUrl,
  release,
} from "./mediaCache.js";

/**
 * A signed URL and the key that opens what is behind it.
 *
 * Not `MediaSource` — the DOM already has a global by that name, for the Media
 * Source Extensions API, and a local interface that shadows it silently unifies
 * with it wherever the import is missing.
 */
export interface MediaRef {
  url: string;
  /** Null for files stored before encryption existed; those render directly. */
  wrappedFileKey: string | null;
  /**
   * The derivative's dimensions, when the server sent them.
   *
   * Resizing preserves aspect, so these are the original's proportions — which
   * is what lets a caller reserve a frame of the right shape *before* the bytes
   * arrive, instead of letterboxing everything into one fixed box or letting
   * the layout jump when the picture decodes.
   */
  width?: number | null;
  height?: number | null;
}

export interface DecryptedUrlState {
  url: string | null;
  /** True while fetching or decrypting, so a tile can hold its skeleton. */
  pending: boolean;
  /** Set when the keyring is locked — the interface offers to unlock. */
  locked: boolean;
  /**
   * Set when object storage refused the request, which is a configuration
   * problem rather than a problem with this photograph.
   *
   * Kept apart from `failed` because the two want opposite responses. A broken
   * derivative is worth rebuilding; rebuilding against a bucket that will not
   * answer is a button that cannot possibly work, offered to somebody who will
   * press it repeatedly and conclude their photographs are gone.
   */
  blocked: boolean;
  failed: boolean;
}

const IDLE: DecryptedUrlState = {
  url: null,
  pending: false,
  locked: false,
  blocked: false,
  failed: false,
};

export function useDecryptedUrl(
  source: MediaRef | null | undefined,
  groupId: string,
  contentType?: string,
): DecryptedUrlState {
  const [state, setState] = useState<DecryptedUrlState>(IDLE);

  const url = source?.url ?? null;
  const wrappedFileKey = source?.wrappedFileKey ?? null;

  useEffect(() => {
    if (!url) {
      setState(IDLE);
      return;
    }

    /*
     * A tile can be recycled onto a different photograph mid-flight — the
     * virtualiser reuses components as rows scroll past. Without this guard the
     * slower of two decryptions wins and the wrong picture appears.
     */
    let current = true;
    let retained: string | null = null;
    setState({
      url: null,
      pending: true,
      locked: false,
      blocked: false,
      failed: false,
    });

    acquireDecryptedUrl({ url, wrappedFileKey, groupId, contentType })
      .then((ready) => {
        if (!current) {
          // Unmounted while decrypting. The retain still happened, so it still
          // has to be given back.
          release(ready);
          return;
        }
        retained = ready;
        setState({
          url: ready,
          pending: false,
          locked: false,
          blocked: false,
          failed: false,
        });
      })
      .catch((error: unknown) => {
        if (!current) return;
        setState({
          url: null,
          pending: false,
          locked: error instanceof LockedError,
          blocked: error instanceof StorageUnreachableError,
          failed: true,
        });
      });

    return () => {
      current = false;
      if (retained) release(retained);
    };
  }, [url, wrappedFileKey, groupId, contentType]);

  return state;
}
